import React, { useState, useEffect } from 'react';
import { X, MessageCircle, Mail, Phone, CheckCircle2 } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { useInquiry } from '../context/InquiryContext';
import { companyInfo } from '../config/themeConfig';
import './InquiryModal.css';

const SuccessModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="success-modal">
      <div className="success-modal__backdrop" onClick={onClose} />
      <div className="success-modal__content">
        <div className="success-modal__icon-wrapper">
          <CheckCircle2 size={80} className="success-modal__icon" />
        </div>
        <h2 className="success-modal__title">Inquiry Submitted Successfully!</h2>
        <p className="success-modal__message">
          Thank you for your inquiry. We'll get back to you soon!
        </p>
        <button onClick={onClose} className="success-modal__button">
          Close
        </button>
      </div>
    </div>
  );
};

const InquiryModal = () => {
  const { isModalOpen, selectedProduct, closeInquiry, getWhatsAppLink, getEmailSubject } = useInquiry();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Initialize EmailJS on component mount
  useEffect(() => {
    emailjs.init('GN0Nsfmmki0BN-1sy'); // Your EmailJS Public Key
  }, []);

  if (!isModalOpen || !selectedProduct) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Inquiry form submitted!');
    setIsSubmitting(true);

    try {
      const templateParams = {
        to_email: companyInfo.email,
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        customer_message: formData.message,
        product_name: selectedProduct?.name || 'Product Inquiry',
        product_model: selectedProduct?.model || '',
        inquiry_date: new Date().toLocaleDateString(),
      };

      console.log('Sending inquiry email with data:', templateParams);

      // Send email to admin with inquiry details
      const response = await emailjs.send(
        'service_nkf798b',
        'template_6z9208c',
        templateParams
      );
      
      console.log('Inquiry email sent successfully:', response);
      setSubmitStatus('success');
      setShowSuccessModal(true);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
      });
      
      // Auto-close success modal after 3 seconds
      setTimeout(() => {
        setShowSuccessModal(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to send inquiry:', error);
      alert('Error: ' + (error.text || error.message || 'Failed to send inquiry'));
      setSubmitStatus('error');
      setTimeout(() => {
        setSubmitStatus(null);
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const whatsappLink = getWhatsAppLink(selectedProduct);
  const emailSubject = getEmailSubject(selectedProduct);

  return (
    <div
      className="inquiry-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="inquiry-modal-title"
    >
      {/* Backdrop */}
      <div
        className="inquiry-modal__backdrop"
        onClick={closeInquiry}
        aria-hidden="true"
      />

      {/* Modal Content */}
      <div className="inquiry-modal__content">
        {/* Close Button */}
        <button
          onClick={closeInquiry}
          className="inquiry-modal__close"
          aria-label="Close modal"
        >
          <X size={20} />
        </button>

        {/* Modal Header */}
        <div className="inquiry-modal__header">
          <div className="inquiry-modal__header-content">
            {/* Product Image */}
            <div className="inquiry-modal__product-image">
              <img src={selectedProduct.image} alt={selectedProduct.name} />
            </div>
            <div className="inquiry-modal__header-info">
              <h2 id="inquiry-modal-title" className="inquiry-modal__title">
                Request Quote
              </h2>
              <p className="inquiry-modal__product-name">{selectedProduct.name}</p>
              <p className="inquiry-modal__product-model">Model: {selectedProduct.model}</p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="inquiry-modal__quick-actions">
          <p className="inquiry-modal__quick-actions-label">Quick Contact Options:</p>
          <div className="inquiry-modal__quick-buttons">
            {/* WhatsApp */}
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inquiry-modal__quick-btn inquiry-modal__quick-btn--whatsapp"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.748.915 5.285 2.549 7.361L1.464 23.89l4.876-1.546C7.91 23.17 9.896 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0m0 21.917c-1.842 0-3.635-.488-5.167-1.34l-.371-.22-3.842 1.22 1.242-4.537-.287-.456c-.963-1.527-1.475-3.285-1.475-5.163 0-5.516 4.492-10.008 10.008-10.008 5.515 0 10.006 4.492 10.006 10.008-.001 5.516-4.492 10.008-10.006 10.008m5.491-7.497c-.3-.149-1.774-.875-2.05-.976-.276-.101-.476-.15-.675.15-.2.3-.775.975-.951 1.175-.175.2-.35.224-.65.075-.3-.15-1.263-.467-2.405-1.487-.89-.796-1.49-1.776-1.665-2.076-.175-.3-.019-.463.132-.612.135-.135.3-.351.45-.525.15-.175.2-.3.3-.5.1-.2.05-.375-.025-.525-.075-.15-.675-1.625-.925-2.225-.244-.587-.494-.508-.675-.517-.176-.008-.376-.01-.576-.01-.2 0-.525.075-.8.375-.275.3-1.05 1.025-1.05 2.5 0 1.475 1.075 2.9 1.225 3.1.15.2 2.112 3.225 5.115 4.518.715.31 1.275.495 1.71.633.72.23 1.375.198 1.89.12.576-.086 1.768-.724 2.016-1.425.248-.7.248-1.3.174-1.425-.074-.125-.274-.2-.574-.35"/>
              </svg>
              <span>WhatsApp Inquiry</span>
            </a>

            {/* Email */}
            <a
              href={`mailto:${companyInfo.email}?subject=${encodeURIComponent(emailSubject)}`}
              className="inquiry-modal__quick-btn inquiry-modal__quick-btn--email"
            >
              <Mail size={18} />
              <span>Email Us</span>
            </a>

            {/* Call */}
            <a
              href={`tel:${companyInfo.phone}`}
              className="inquiry-modal__quick-btn inquiry-modal__quick-btn--call"
            >
              <Phone size={18} />
              <span>Call Now</span>
            </a>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="inquiry-modal__form">
          <p className="inquiry-modal__form-label">
            Or fill out this form and we'll get back to you:
          </p>

          {submitStatus === 'error' && (
            <div className="inquiry-modal__status inquiry-modal__status--error">
              Something went wrong. Please try again or use the quick contact options above.
            </div>
          )}

          <div className="inquiry-modal__fields">
            {/* Name and Phone Row */}
            <div className="inquiry-modal__field-row">
              {/* Name */}
              <div className="inquiry-modal__field">
                <label htmlFor="name" className="inquiry-modal__field-label">
                  Your Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="inquiry-modal__input"
                  placeholder="Enter your name"
                />
              </div>

              {/* Phone */}
              <div className="inquiry-modal__field">
                <label htmlFor="phone" className="inquiry-modal__field-label">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="inquiry-modal__input"
                  placeholder="Enter your phone number"
                />
              </div>
            </div>

            {/* Email */}
            <div className="inquiry-modal__field">
              <label htmlFor="email" className="inquiry-modal__field-label">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="inquiry-modal__input"
                placeholder="Enter your email (optional)"
              />
            </div>

            {/* Subject (Auto-populated, readonly) */}
            <div className="inquiry-modal__field">
              <label htmlFor="subject" className="inquiry-modal__field-label">
                Subject
              </label>
              <input
                type="text"
                id="subject"
                value={emailSubject}
                readOnly
                className="inquiry-modal__input inquiry-modal__input--readonly"
              />
            </div>

            {/* Message */}
            <div className="inquiry-modal__field">
              <label htmlFor="message" className="inquiry-modal__field-label">
                Additional Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={3}
                className="inquiry-modal__textarea"
                placeholder="Any specific requirements or questions?"
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="inquiry-modal__submit"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
          </button>
        </form>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          closeInquiry();
          setFormData({ name: '', email: '', phone: '', message: '' });
        }}
      />
    </div>
  );
};

export default InquiryModal;
